import { removeRedundantObjects } from 'utils/group';
import {
  listBrandUsers,
  listGroupUsers,
  listUsers,
  searchOrgUsers,
  searchGroupUsers,
  inviteUser,
  updateUser,
  deleteUser,
  addMember,
  removeMember,
  assignAdmin,
  unassignAdmin,
} from '../thunks/organization-users';

export const organizationUsersReducers = (builder) => {
  builder
    .addCase(listBrandUsers.fulfilled, (state, action) => {
      const { context, size, offset } = action.meta.arg;
      const users = action.payload;

      if (offset) {
        const updateUsers = [...state.brandUsers[context].value, ...users];
        state.brandUsers[context].value = removeRedundantObjects({ array: updateUsers, property: 'id' });
      } else {
        state.brandUsers[context].value = users;
      }
      state.brandUsers[context].canLoadMore = users.length === size;
      state.brandUsers[context].loading = false;
    })
    .addCase(listBrandUsers.pending, (state, action) => {
      const { context } = action.meta.arg;
      state.brandUsers[context].loading = true;
    })
    .addCase(listBrandUsers.rejected, (state, action) => {
      const { context } = action.meta.arg;
      state.brandUsers[context].loading = false;
    });

  builder
    .addCase(listGroupUsers.fulfilled, (state, action) => {
      const users = action.payload ?? [];
      const requestSize = action.meta.arg.size;
      const offset = action.meta.arg.offset;
      const pageSize = Math.max(1, requestSize - 1);

      const hasMoreNow = users.length > pageSize;
      const pageRows = hasMoreNow ? users.slice(0, pageSize) : users;

      const existingMembers = offset === 0 ? [] : state.selectedGroup.members.value;
      const updateMembers = [...existingMembers, ...pageRows];

      state.selectedGroup.members.value = removeRedundantObjects({
        array: updateMembers,
        property: 'id',
      });

      state.selectedGroup.members.canLoadMore = hasMoreNow;
      state.selectedGroup.loading = false;
    })
    .addCase(listGroupUsers.pending, (state) => {
      state.selectedGroup.loading = true;
    })
    .addCase(listGroupUsers.rejected, (state) => {
      state.selectedGroup.loading = false;
    });

  builder
    .addCase(listUsers.fulfilled, (state, action) => {
      const users = action.payload;
      state.userList.value = removeRedundantObjects({ array: users, property: 'id' });
      state.userList.loading = false;
    })
    .addCase(listUsers.pending, (state) => {
      state.userList.loading = true;
    })
    .addCase(listUsers.rejected, (state) => {
      state.userList.loading = false;
    });

  builder
    .addCase(searchOrgUsers.fulfilled, (state) => {
      state.brandUsers.table.searchLoading = false;
    })
    .addCase(searchOrgUsers.pending, (state) => {
      state.brandUsers.table.searchLoading = true;
    })
    .addCase(searchOrgUsers.rejected, (state) => {
      state.brandUsers.table.searchLoading = false;
    });

  builder
    .addCase(searchGroupUsers.fulfilled, (state, action) => {
      const { search, size: requestSize } = action.meta.arg;
      const rows = action.payload ?? [];

      const pageSize = Math.max(1, (requestSize ?? 1) - 1);
      const hasMoreNow = rows.length > pageSize;
      const pageRows = hasMoreNow ? rows.slice(0, pageSize) : rows;

      const isNewSearch = search !== state.selectedGroup.memberSearch.query;
      state.selectedGroup.memberSearch.query = search;

      const updatedSearchResults = isNewSearch ? pageRows : [...state.selectedGroup.memberSearch.value, ...pageRows];

      state.selectedGroup.memberSearch.value = removeRedundantObjects({
        array: updatedSearchResults,
        property: 'id',
      });

      state.selectedGroup.memberSearch.canLoadMore = hasMoreNow;
      state.selectedGroup.loadingSearch = false;
    })
    .addCase(searchGroupUsers.pending, (state) => {
      state.selectedGroup.loadingSearch = true;
    })
    .addCase(searchGroupUsers.rejected, (state) => {
      state.selectedGroup.loadingSearch = false;
    });

  builder
    .addCase(inviteUser.fulfilled, (state, action) => {
      const user = action.payload;
      const updateBrandUsersTable = [...state.brandUsers.table.value, user];
      state.brandUsers.table.value = removeRedundantObjects({ array: updateBrandUsersTable, property: 'id' });
      state.brandUsers.updating = false;
    })
    .addCase(inviteUser.pending, (state) => {
      state.brandUsers.updating = true;
    })
    .addCase(inviteUser.rejected, (state) => {
      state.brandUsers.updating = false;
    });

  builder
    .addCase(updateUser.fulfilled, (state, action) => {
      const user = action.payload;
      const updateUsersTable = state.brandUsers.table.value.map((g) => (g.id === user.id ? user : g));
      state.brandUsers.table.value = updateUsersTable;
      state.brandUsers.updating = false;
    })
    .addCase(updateUser.pending, (state) => {
      state.brandUsers.updating = true;
    })
    .addCase(updateUser.rejected, (state) => {
      state.brandUsers.updating = false;
    });

  builder
    .addCase(deleteUser.fulfilled, (state, action) => {
      const userId = action.payload;
      state.brandUsers.table.value = state.brandUsers.table.value.filter((user) => user.id !== userId);
      state.brandUsers.selector.value = state.brandUsers.selector.value.filter((user) => user.id !== userId);
      state.brandUsers.updating = false;
    })
    .addCase(deleteUser.pending, (state) => {
      state.brandUsers.updating = true;
    })
    .addCase(deleteUser.rejected, (state) => {
      state.brandUsers.updating = false;
    });

  builder
    .addCase(addMember.fulfilled, (state, action) => {
      const updatedUser = action.payload;
      const updateUsersTable = state.brandUsers.table.value.map((user) =>
        user.id === updatedUser.id ? { ...user, organisations: updatedUser.organisations } : user
      );

      if (state.selectedGroup?.members?.value) {
        const updateMembers = [updatedUser, ...state.selectedGroup.members.value];
        state.selectedGroup.members.value = removeRedundantObjects({ array: updateMembers, property: 'id' });
      }
      if (state.selectedGroup?.memberSearch?.value) {
        const updateMemberSearch = [updatedUser, ...state.selectedGroup.memberSearch.value];
        state.selectedGroup.memberSearch.value = removeRedundantObjects({ array: updateMemberSearch, property: 'id' });
      }
      state.selectedUser = { ...state.selectedUser, organisations: updatedUser.organisations };
      state.brandUsers.table.value = updateUsersTable;
      state.updatingMembers = false;
    })
    .addCase(addMember.pending, (state) => {
      state.updatingMembers = true;
    })
    .addCase(addMember.rejected, (state) => {
      state.updatingMembers = false;
    });

  builder
    .addCase(removeMember.fulfilled, (state, action) => {
      const updatedUser = action.payload;
      const updateUsersTable = state.brandUsers.table.value.map((user) =>
        user.id === updatedUser.id ? { ...user, organisations: updatedUser.organisations } : user
      );

      if (state.selectedGroup?.members?.value) {
        state.selectedGroup.members.value = state.selectedGroup.members.value.filter(
          (user) => user.id !== updatedUser.id
        );
      }
      if (state.selectedGroup?.memberSearch?.value) {
        state.selectedGroup.memberSearch.value = state.selectedGroup.memberSearch.value.filter(
          (user) => user.id !== updatedUser.id
        );
      }
      state.selectedUser = { ...state.selectedUser, organisations: updatedUser.organisations };
      state.brandUsers.table.value = updateUsersTable;
      state.updatingMembers = false;
    })
    .addCase(removeMember.pending, (state) => {
      state.updatingMembers = true;
    })
    .addCase(removeMember.rejected, (state) => {
      state.updatingMembers = false;
    });

  builder
    .addCase(assignAdmin.fulfilled, (state, action) => {
      const updateOrg = action.payload;
      if (updateOrg?.group_type) {
        const updateGroups = state.brandGroups.value.map((group) =>
          group.id === updateOrg.id ? { ...group, admins: updateOrg.admins } : group
        );
        state.selectedGroup.value = { ...state.selectedGroup.value, admins: updateOrg.admins };
        state.brandGroups.value = updateGroups;
      } else {
        const updateBrand = { ...state.brand.value, admins: updateOrg.admins };
        state.brand.value = updateBrand;
      }
      state.updatingAdmins = false;
    })
    .addCase(assignAdmin.pending, (state) => {
      state.updatingAdmins = true;
    })
    .addCase(assignAdmin.rejected, (state) => {
      state.updatingAdmins = false;
    });

  builder
    .addCase(unassignAdmin.fulfilled, (state, action) => {
      const updateOrg = action.payload;
      if (updateOrg?.group_type) {
        const updateGroups = state.brandGroups.value.map((group) =>
          group.id === updateOrg.id ? { ...group, admins: updateOrg.admins } : group
        );
        state.selectedGroup.value = { ...state.selectedGroup.value, admins: updateOrg.admins };
        state.brandGroups.value = updateGroups;
      } else {
        const updateBrand = { ...state.brand.value, admins: updateOrg.admins };
        state.brand.value = updateBrand;
      }
      state.updatingAdmins = false;
    })
    .addCase(unassignAdmin.pending, (state) => {
      state.updatingAdmins = true;
    })
    .addCase(unassignAdmin.rejected, (state) => {
      state.updatingAdmins = false;
    });
};
